import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ReferenceLine, Legend } from 'recharts';
import { Scale, Activity, Zap, ArrowUp, ArrowDown } from 'lucide-react';
// Mock Data
const imbalanceData = Array.from({
  length: 24
}, (_, i) => {
  const imbalance = Math.random() * 1000 - 500;
  return {
    time: `${i}:00`,
    imbalance,
    price: imbalance > 0 ? 40 - imbalance / 20 : 40 + Math.abs(imbalance) / 10,
    frequency: 50 + (Math.random() * 0.1 - 0.05)
  };
});
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-799">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-800">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-801">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-802" />
            <span className="text-slate-600 capitalize" data-id="element-803">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-804">
              {entry.value.toFixed(2)}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function BalancingMarketDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-orange-50/30" data-id="element-805">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-806">
        <div data-id="element-807">
          <div className="flex items-center gap-2 mb-1" data-id="element-808">
            <div className="p-2 bg-orange-500 rounded-lg" data-id="element-809">
              <Scale size={20} className="text-white" data-id="element-810" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-811">
              Balancing Market
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-812">
            System frequency, imbalance volumes, and regulation prices
          </p>
        </div>

        <div className="flex items-center gap-4" data-id="element-813">
          <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm text-center" data-id="element-814">
            <p className="text-xs text-slate-500 font-bold" data-id="element-815">System Frequency</p>
            <p className="text-xl font-mono font-bold text-emerald-600" data-id="element-816">
              50.02 Hz
            </p>
          </div>
          <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm text-center" data-id="element-817">
            <p className="text-xs text-slate-500 font-bold" data-id="element-818">Net Imbalance</p>
            <p className="text-xl font-mono font-bold text-orange-600" data-id="element-819">
              +240 MW
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-820">
        {/* Imbalance Volume & Price */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="lg:col-span-2 bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-821">
          <div className="flex justify-between items-center mb-6" data-id="element-822">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-823">
              Imbalance Volume & Price
            </h3>
            <div className="flex gap-2 text-xs" data-id="element-824">
              <span className="flex items-center gap-1" data-id="element-825">
                <div className="w-2 h-2 rounded-full bg-orange-500" data-id="element-826"></div>{' '}
                Volume
              </span>
              <span className="flex items-center gap-1" data-id="element-827">
                <div className="w-2 h-2 rounded-full bg-slate-800" data-id="element-828"></div> Price
              </span>
            </div>
          </div>

          <div className="h-[350px] w-full" data-id="element-829">
            <ResponsiveContainer width="100%" height="100%" data-id="element-830">
              <BarChart data={imbalanceData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-831">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-832" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-833" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} label={{
                value: 'MW',
                angle: -90,
                position: 'insideLeft',
                fontSize: 10
              }} data-id="element-834" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} label={{
                value: '€/MWh',
                angle: 90,
                position: 'insideRight',
                fontSize: 10
              }} data-id="element-835" />
                <Tooltip content={<CustomTooltip data-id="element-837" />} data-id="element-836" />
                <ReferenceLine y={0} yAxisId="left" stroke="#94a3b8" data-id="element-838" />
                <Bar yAxisId="left" dataKey="imbalance" fill="#f97316" radius={[2, 2, 0, 0]} barSize={12} data-id="element-839" />
                <Line yAxisId="right" type="step" dataKey="price" stroke="#1e293b" strokeWidth={2} dot={false} data-id="element-840" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* System State */}
        <div className="space-y-6" data-id="element-841">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-842">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-843">
              Current State
            </h3>
            <div className="flex flex-col items-center justify-center py-6" data-id="element-844">
              <div className="w-32 h-32 rounded-full border-4 border-emerald-100 flex items-center justify-center relative" data-id="element-845">
                <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" data-id="element-846"></div>
                <div className="text-center" data-id="element-847">
                  <span className="block text-2xl font-bold text-slate-900" data-id="element-848">
                    Long
                  </span>
                  <span className="text-xs text-slate-500" data-id="element-849">System State</span>
                </div>
              </div>
              <div className="mt-6 w-full space-y-3" data-id="element-850">
                <div className="flex justify-between text-sm" data-id="element-851">
                  <span className="text-slate-600" data-id="element-852">aFRR Activated</span>
                  <span className="font-bold text-slate-900" data-id="element-853">+120 MW</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-854">
                  <span className="text-slate-600" data-id="element-855">mFRR Activated</span>
                  <span className="font-bold text-slate-900" data-id="element-856">0 MW</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-857">
            <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-858">
              Price Forecast
            </h3>
            <div className="flex items-baseline gap-2 mb-1" data-id="element-859">
              <span className="text-3xl font-bold text-orange-600" data-id="element-860">€62.50</span>
              <span className="text-sm text-slate-500" data-id="element-861">/ MWh</span>
            </div>
            <p className="text-xs text-slate-400" data-id="element-862">
              Predicted imbalance price for next ISP
            </p>
          </motion.div>
        </div>
      </div>
    </div>;
}